import { commonModuleConfig } from '@/config/module-config';
import { spModuleConfig, spHiddenModuleConfig } from '@/config/sp-module-config';
import { ModuleName, ModuleConfig, EXTRA_SORT_WEIGHT, SORT_WEIGHT } from '@/types/module-config';
import { CustomException } from '@tencent/ppd-base-libs';
import { defineStore } from 'pinia';
import { reactive } from 'vue';

/** 模块初始化参数 */
export type InitEmitMessage = {
  status: 'fail' | 'success',
  moduleName: ModuleName,
  message?: any;
};

type SortParams = Pick<ModuleConfig, 'sort' | 'extraSort'>;

const initModuleConfig: Array<ModuleConfig> = commonModuleConfig;

export const useModuleManagerStore = defineStore('moduleManager', {
  state: () => ({
    moduleConfig: initModuleConfig,
    /** 已初始化的模块：该字段用于弹窗管理统计（不一定所有模块都有登记inited） */
    initedModule: [] as InitEmitMessage[],
  }),
  getters: {
    sortedModuleConfig(state) {
      return state.moduleConfig.sort((a: SortParams, b: SortParams) => (getModuleSort(a) < getModuleSort(b) ? 1 : -1));
    },
  },
  actions: {
    /** 根据spId获取商户专属模块配置 */
    getSpModuleConfig(spId: string): ModuleConfig[] {
      return spModuleConfig.filter(item => item.spId === spId);
    },
    /** 根据spId获取需要屏蔽的模块名称列表 */
    getHiddenModuleNames(spId: string): ModuleName[] {
      return spHiddenModuleConfig
        .filter(item => item.spId === spId)
        .map(item => item.moduleName);
    },
    /** 新增或更新模块配置 */
    addOrUpdateModuleConfigs(newConfigs: ModuleConfig[], hiddenModuleNames: ModuleName[] = []) {
      const addModuleConfig = (newConfig: ModuleConfig) => {
        if (!newConfig?.moduleName || !newConfig.component) {
          return;
        }
        const isModuleExisted = this.moduleConfig.find(item => item.moduleName === newConfig.moduleName);
        if (isModuleExisted) {
          // 如果已存在同名模块，则不添加,尝试更新配置
          console.log(`[addModuleConfig]:${newConfig.moduleName}模块已存在`);
          this.updateModuleConfig(newConfig);
          return;
        }
        this.moduleConfig = this.moduleConfig.concat(reactive(newConfig));
      };
      newConfigs.forEach(item => addModuleConfig(item));

      // 移除需要屏蔽的模块
      if (hiddenModuleNames.length > 0) {
        this.moduleConfig = this.moduleConfig.filter(item => !hiddenModuleNames.includes(item.moduleName));
        console.log(`[addOrUpdateModuleConfigs]:已屏蔽模块:${hiddenModuleNames.join(',')}`);
      }
    },
    /** 更新模块配置 */
    updateModuleConfig(updateConfig: Partial<Omit<ModuleConfig, 'component'>>) {
      const { moduleName } = updateConfig;
      const targetModule = this.moduleConfig.find(item => item.moduleName === moduleName);
      if (!targetModule || !moduleName) {
        return;
      }
      const newModuleConfig = reactive({ ...targetModule, ...updateConfig });
      this.moduleConfig = this.moduleConfig.filter(item => item.moduleName !== moduleName).concat(newModuleConfig);
    },
    /** 更新“已初始化”模块 */
    updateInitedModule(arg: InitEmitMessage) {
      const initedModuleNames = this.initedModule.map(item => item.moduleName);
      if (initedModuleNames.includes(arg.moduleName)) {
        new CustomException('', 'updateInitedModule', `该模块被重复初始化:${arg.moduleName}`);
        return;
      }
      this.initedModule.push(arg);
    },
  },
});

/** 計算模塊排序值 */
const getModuleSort = ({ sort, extraSort }: SortParams) => {
  const finalSort = (extraSort ?? 0) * EXTRA_SORT_WEIGHT + (sort ?? 0) * SORT_WEIGHT;
  return finalSort;
};
